<script>
import { useAlert, useTrack } from 'dashboard/composables';
import AiAgentMetrics from './components/AiAgentMetrics.vue';
import AiAgentTable from './components/AiAgentTable.vue';
import ReportFilters from './components/ReportFilters.vue';
import { GROUP_BY_FILTER } from './constants';
import { AI_AGENT_METRICS } from 'dashboard/store/modules/reports';
import ReportContainer from './ReportContainer.vue';
import { REPORTS_EVENTS } from '../../../../helper/AnalyticsHelper/events';
import ReportHeader from './components/ReportHeader.vue';
import ReportsAPI from 'dashboard/api/reports';

export default {
  name: 'AiAgentReports',
  components: {
    AiAgentMetrics,
    AiAgentTable,
    ReportHeader,
    ReportFilters,
    ReportContainer,
  },
  data() {
    return {
      from: 0,
      to: 0,
      groupBy: GROUP_BY_FILTER[1],
      // KEY (i18n REPORT.METRICS.<KEY>) -> métrica do backend.
      reportKeys: AI_AGENT_METRICS.reduce(
        (acc, metric) => ({ ...acc, [metric.toUpperCase()]: metric }),
        {}
      ),
      isCheckingWorkflowHealth: false,
      workflowHealth: [],
      funnel: null,
    };
  },
  methods: {
    async fetchFunnel() {
      try {
        const response = await ReportsAPI.getAiAgentsFunnel(
          this.getRequestPayload()
        );
        this.funnel = response.data;
      } catch {
        useAlert(this.$t('REPORT.DATA_FETCHING_FAILED'));
      }
    },
    funnelStageLabel(eventType) {
      return this.$t(
        `AI_AGENT_REPORTS.FUNNEL.STAGES.${eventType.toUpperCase()}`
      );
    },
    funnelTransitionKey(fromType, toType) {
      return `${fromType}_para_${toType}`;
    },
    funnelDropOff(fromType, toType) {
      return this.funnel?.drop_off_percent?.[
        this.funnelTransitionKey(fromType, toType)
      ];
    },
    funnelAvgHours(fromType, toType) {
      return this.funnel?.avg_hours_between_stages?.[
        this.funnelTransitionKey(fromType, toType)
      ];
    },
    async runWorkflowHealthCheck() {
      this.isCheckingWorkflowHealth = true;
      try {
        const response = await ReportsAPI.getAiAgentsWorkflowHealth();
        this.workflowHealth = response.data;
        useTrack(REPORTS_EVENTS.FILTER_REPORT, {
          reportType: 'ai_agents_workflow_health',
        });
      } catch {
        useAlert(this.$t('AI_AGENT_REPORTS.WORKFLOW_HEALTH.ERROR'));
      } finally {
        this.isCheckingWorkflowHealth = false;
      }
    },
    workflowStatusLabel(status) {
      return this.$t(
        `AI_AGENT_REPORTS.WORKFLOW_HEALTH.STATUS_${status.toUpperCase()}`
      );
    },
    fetchAllData() {
      this.fetchAiAgentsSummary();
      this.fetchChartData();
      this.fetchFunnel();
    },
    fetchAiAgentsSummary() {
      try {
        this.$store.dispatch('fetchAiAgentsSummary', this.getRequestPayload());
      } catch {
        useAlert(this.$t('REPORT.SUMMARY_FETCHING_FAILED'));
      }
    },
    fetchChartData() {
      Object.values(this.reportKeys).forEach(async metric => {
        try {
          await this.$store.dispatch('fetchAiAgentsReport', {
            metric,
            ...this.getRequestPayload(),
          });
        } catch {
          useAlert(this.$t('REPORT.DATA_FETCHING_FAILED'));
        }
      });
    },
    getRequestPayload() {
      const { from, to, groupBy } = this;
      return { from, to, groupBy: groupBy?.period };
    },
    onFilterChange({ from, to, groupBy }) {
      this.from = from;
      this.to = to;
      this.groupBy = groupBy;
      this.fetchAllData();

      useTrack(REPORTS_EVENTS.FILTER_REPORT, {
        filterValue: { from, to, groupBy },
        reportType: 'ai_agents',
      });
    },
  },
};
</script>

<template>
  <ReportHeader :header-title="$t('AI_AGENT_REPORTS.HEADER')" />
  <div class="flex flex-col gap-4">
    <ReportFilters
      :show-entity-filter="false"
      show-group-by
      :show-business-hours="false"
      @filter-change="onFilterChange"
    />

    <AiAgentMetrics />
    <ReportContainer
      account-summary-key="getAiAgentsSummary"
      summary-fetching-key="getAiAgentsSummaryFetchingStatus"
      :group-by="groupBy"
      :report-keys="reportKeys"
    />
    <AiAgentTable :group-by="groupBy" />

    <div
      class="px-6 py-5 overflow-x-auto shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
    >
      <h3 class="mb-1 text-base font-medium text-n-slate-12">
        {{ $t('AI_AGENT_REPORTS.FUNNEL.TITLE') }}
      </h3>
      <p class="mb-4 text-sm text-n-slate-11">
        {{ $t('AI_AGENT_REPORTS.FUNNEL.SUBTITLE') }}
      </p>

      <div
        v-if="funnel && funnel.stages.some(stage => stage.count > 0)"
        class="flex flex-wrap items-stretch gap-2"
      >
        <template
          v-for="(stage, index) in funnel.stages"
          :key="stage.event_type"
        >
          <div
            class="flex flex-col justify-center min-w-[140px] px-4 py-3 border rounded-lg border-n-container bg-n-solid-3"
          >
            <span class="text-xs text-n-slate-11">
              {{ funnelStageLabel(stage.event_type) }}
            </span>
            <span class="text-2xl font-semibold tabular-nums text-n-slate-12">
              {{ stage.count }}
            </span>
          </div>

          <div
            v-if="index < funnel.stages.length - 1"
            class="flex flex-col items-center justify-center px-1 text-xs text-n-slate-11 whitespace-nowrap"
          >
            <span>→</span>
            <span
              v-if="
                funnelDropOff(stage.event_type, funnel.stages[index + 1].event_type) !== null &&
                  funnelDropOff(stage.event_type, funnel.stages[index + 1].event_type) !== undefined
              "
            >
              -{{
                funnelDropOff(
                  stage.event_type,
                  funnel.stages[index + 1].event_type
                )
              }}%
            </span>
            <span
              v-if="
                funnelAvgHours(stage.event_type, funnel.stages[index + 1].event_type) !== null &&
                  funnelAvgHours(stage.event_type, funnel.stages[index + 1].event_type) !== undefined
              "
            >
              {{ $t('AI_AGENT_REPORTS.FUNNEL.AVG_TIME') }}:
              {{
                funnelAvgHours(
                  stage.event_type,
                  funnel.stages[index + 1].event_type
                )
              }}{{ $t('AI_AGENT_REPORTS.FUNNEL.HOURS') }}
            </span>
          </div>
        </template>
      </div>
      <p v-else class="text-sm text-n-slate-11">
        {{ $t('AI_AGENT_REPORTS.FUNNEL.NO_DATA') }}
      </p>
    </div>

    <div
      class="px-6 py-5 overflow-x-auto shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
    >
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-base font-medium text-n-slate-12">
          {{ $t('AI_AGENT_REPORTS.WORKFLOW_HEALTH.COLUMN_NAME') }}
        </h3>
        <woot-button
          size="small"
          variant="smooth"
          :is-loading="isCheckingWorkflowHealth"
          @click="runWorkflowHealthCheck"
        >
          {{
            isCheckingWorkflowHealth
              ? $t('AI_AGENT_REPORTS.WORKFLOW_HEALTH.RUNNING')
              : $t('AI_AGENT_REPORTS.WORKFLOW_HEALTH.BUTTON')
          }}
        </woot-button>
      </div>

      <table v-if="workflowHealth.length" class="min-w-full text-sm border-collapse">
        <thead>
          <tr class="text-left border-b border-n-container">
            <th class="py-2 pr-4 font-medium text-n-slate-11">
              {{ $t('AI_AGENT_REPORTS.WORKFLOW_HEALTH.COLUMN_NAME') }}
            </th>
            <th class="px-3 py-2 font-medium text-n-slate-11">
              {{ $t('AI_AGENT_REPORTS.WORKFLOW_HEALTH.COLUMN_STATUS') }}
            </th>
            <th class="px-3 py-2 font-medium text-right text-n-slate-11 whitespace-nowrap">
              {{
                $t('AI_AGENT_REPORTS.WORKFLOW_HEALTH.COLUMN_LAST_EXECUTION')
              }}
            </th>
            <th class="px-3 py-2 font-medium text-right text-n-slate-11 whitespace-nowrap">
              {{ $t('AI_AGENT_REPORTS.WORKFLOW_HEALTH.COLUMN_HOURS_SINCE') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in workflowHealth"
            :key="item.workflow_id"
            class="border-b border-n-container/60"
          >
            <td class="py-2 pr-4 text-n-slate-12 whitespace-nowrap">
              {{ item.name }}
            </td>
            <td class="px-3 py-2 text-n-slate-11">
              {{ workflowStatusLabel(item.status) }}
            </td>
            <td class="px-3 py-2 text-right tabular-nums text-n-slate-11 whitespace-nowrap">
              {{ item.last_started_at || '—' }}
            </td>
            <td class="px-3 py-2 text-right tabular-nums text-n-slate-11">
              {{ item.hours_since_last_execution ?? '—' }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
